import graphql__90715205d0ec576db35e from "./__generated__/useMissingSelectedUserLoaderQuery.graphql.js";import { useEffect } from "react";
import {
  graphql,
  useRelayEnvironment,
  fetchQuery,
  ConnectionHandler,
  commitLocalUpdate
} from "react-relay";

import {
  RESOURCES_LIST_USER_TYPE,
  useResourcesList
} from "../../ResourcesList/resourcesList";
import { selectedItem } from "../../Utils/resourcesList";

// This hook handles the case where the map users resources list selection is updated to
// target a user that is not already loaded in the MapUsersList component edges (when the
// visitor clicks a user marker). It responds to the selection update and loads the
// missing user data and appends it to the users list, which automatically makes the
// sidebar scroll to the newsly loaded user as it's already selected.
//
export const useMissingSelectedUserLoader = usersMap => {
  const { id: usersMapId } = usersMap;

  const resourcesList = useResourcesList();
  const itemSelected = selectedItem(resourcesList);
  const itemSelectedType = itemSelected?.itemType;
  const itemSelectedId = itemSelected?.itemId;

  // Create a flat list of loaded user ids that we'll use to check whether the currently
  // selected user is already loaded or not. Note that the flat list of ids works better
  // to be used in a useEffect hook as it's better handled in the dependencies array than
  // a list of complex objects which object identifier can change over time.
  const loadedUserIds = usersMap.usersMapUsers.edges.map(({ node }) => node.user.id);

  // Get the Realy environment so we can provide it to the Relay runtime functions
  const relayEnvironment = useRelayEnvironment();

  useEffect(() => {
    // Ensure that there's a selected item and that it's a user, it can also be a map
    // element, which we don't want to handle here.
    if (itemSelectedType !== RESOURCES_LIST_USER_TYPE) return;

    // Check whether the selected user is loaded or not, and if it's already loaded, don't
    // do anything
    const isSelectedUserLoaded = loadedUserIds.some(userId => itemSelectedId === userId);
    if (isSelectedUserLoaded) return;

    // If the user is not loaded, fetch the user's data from the server and append it to
    // the users list.
    fetchQuery(relayEnvironment, useMissingSelectedUserLoaderQuery, {
      userId: itemSelectedId,
      usersMapId
    }).subscribe({
      next: ({ usersMapUser }) => {
        // The commitLocalUpdate yields a `RecordSourceSelectorProxy` instance as the
        // first callback argument, that we name `store`, and allows us to locally update
        // the store without making any server-side mutation
        commitLocalUpdate(relayEnvironment, store => {
          // Get the connection and UsersMapUser records from the store to allow
          // manipulating them in the store
          const connectionRecord = store.get(usersMap.usersMapUsers.__id);

          // Relay ensures that the `fetchQuery` network request is only run once, but may
          // call the `next` callback multiple times in case our useEffect hook is called
          // twice, so we ensure on our own that the record is not already added the to
          // connection.
          const recordAlreadyExists = connectionRecord
            .getLinkedRecords("edges")
            .some(edge => edge.getLinkedRecord("node").getDataID() === usersMapUser.id);

          if (recordAlreadyExists) return;

          const usersMapUserRecord = store.get(usersMapUser.id);

          // Build an edge record from the UsersMapUser record, which is needed to append
          // a record to a connection, as connections have edges as direct-child records
          // and not the records themselves.
          const newEdge = ConnectionHandler.createEdge(
            store,
            connectionRecord,
            usersMapUserRecord,
            "UsersMapUserEdge"
          );

          // Add edge at the start of the connection edges list to avoid the scroll down
          // to make the InfinitelyScrollableContainer load the next edges page at the
          // same time
          ConnectionHandler.insertEdgeBefore(connectionRecord, newEdge);
        });
      }
    });
  }, [
    itemSelectedId,
    itemSelectedType,
    loadedUserIds,
    relayEnvironment,
    usersMap,
    usersMapId
  ]);
};

const useMissingSelectedUserLoaderQuery = graphql__90715205d0ec576db35e;
